export interface ContractFilterCriteria {
  clientId: string | "all";
  difficulty: number | "all";
  search: string;
}

/**
 * Filters board items by client, difficulty and a case-insensitive title search.
 * `titleOf` resolves an item's title to the current language. Pure: returns a new
 * array, never mutates the input.
 */
export function filterContracts<T extends { clientId: string; difficulty: number }>(
  items: readonly T[],
  criteria: ContractFilterCriteria,
  titleOf: (item: T) => string,
): T[] {
  const needle = criteria.search.trim().toLowerCase();
  return items.filter((item) => {
    if (criteria.clientId !== "all" && item.clientId !== criteria.clientId) {
      return false;
    }
    if (criteria.difficulty !== "all" && item.difficulty !== criteria.difficulty) {
      return false;
    }
    if (needle !== "" && !titleOf(item).toLowerCase().includes(needle)) {
      return false;
    }
    return true;
  });
}

export interface ClientGroup<T> {
  clientId: string;
  items: T[];
}

/** Groups items by clientId, preserving the order clients first appear in. */
export function groupByClient<T extends { clientId: string }>(items: readonly T[]): ClientGroup<T>[] {
  const groups: ClientGroup<T>[] = [];
  const byClient = new Map<string, ClientGroup<T>>();
  for (const item of items) {
    const existing = byClient.get(item.clientId);
    if (existing) {
      existing.items.push(item);
    } else {
      const group: ClientGroup<T> = { clientId: item.clientId, items: [item] };
      byClient.set(item.clientId, group);
      groups.push(group);
    }
  }
  return groups;
}
